import { useQuery } from "@tanstack/react-query";
import { useMutationWithToast } from "@/hooks/shared/use-mutation-with-toast";
import { API_BASE_URL } from "@/lib/api";
import { api } from "@/lib/api-typed";
import type {
  CertificateType,
} from "@/types";

// Lembrete manual do RH: notifica por email + in-app quem não concluiu
// a formação obrigatória. Acção deliberada (≠ cron diário, que é só in-app).
export const useRemindPendingTrainees = () =>
  useMutationWithToast({
    mutationFn: (trainingId: string) =>
      api.post("/trainings/{training_id}/remind-pending", { path: { training_id: trainingId } }),
  });

// ========== Training Mutations ==========

export const useCreateTraining = () => {
  return useMutationWithToast({
    mutationFn: async (data: {
      title: string;
      description?: string | null;
      content?: string;
      training_type: string;
      structure_type?: "simple" | "structured";
      is_mandatory?: boolean;
      is_active?: boolean;
      is_onboarding?: boolean;
      has_certificate?: boolean;
      certificate_type?: CertificateType | null;
      category_ids?: string[];
      department_ids?: string[];
    }) => {
      return api.post("/trainings/", { body: data });
    },
    invalidateQueries: [
      ["trainings"],
      ["trainings", "stats"],
    ],
  });
};

export const useUpdateTraining = () => {
  return useMutationWithToast({
    mutationFn: async (data: {
      id: string;
      title?: string;
      description?: string | null;
      content?: string;
      training_type?: string;
      structure_type?: "simple" | "structured";
      is_mandatory?: boolean;
      is_active?: boolean;
      is_onboarding?: boolean;
      has_certificate?: boolean;
      certificate_type?: CertificateType | null;
      category_ids?: string[];
      department_ids?: string[];
    }) => {
      const { id, ...updateData } = data;
      return api.patch("/trainings/{training_id}", { path: { training_id: id }, body: updateData });
    },
    invalidateQueries: (_, variables) => [
      ["trainings"],
      ["trainings", variables.id],
      ["trainings", "stats"],
    ],
  });
};

export const useDeleteTraining = () => {
  return useMutationWithToast({
    mutationFn: async (id: string) => {
      return api.delete("/trainings/{training_id}", { path: { training_id: id } });
    },
    invalidateQueries: [
      ["trainings"],
      ["trainings", "stats"],
    ],
  });
};

export const useCompleteTraining = () => {
  return useMutationWithToast({
    mutationFn: async (data: { training_id: string; score?: number }) => {
      return api.post("/trainings/{training_id}/complete", { path: { training_id: data.training_id }, body: { score: data.score } });
    },
    invalidateQueries: (_, variables) => [
      ["trainings", variables.training_id],
      ["trainings", "my-trainings"],
      ["training-completions", variables.training_id],
      ["onboarding-status"],
    ],
  });
};

// ========== Category Mutations ==========

export const useCreateCategory = () => {
  return useMutationWithToast({
    mutationFn: async (data: {
      name: string;
      description?: string;
      parent_id?: string;
      sort_order?: number;
    }) => {
      return api.post("/trainings/categories", { body: data });
    },
    invalidateQueries: [
      ["training-categories"],
    ],
  });
};

export const useUpdateCategory = () => {
  return useMutationWithToast({
    mutationFn: async (data: {
      id: string;
      name?: string;
      description?: string;
      parent_id?: string;
      sort_order?: number;
    }) => {
      const { id, ...updateData } = data;
      return api.patch("/trainings/categories/{category_id}", { path: { category_id: id }, body: updateData });
    },
    invalidateQueries: [
      ["training-categories"],
    ],
  });
};

export const useDeleteCategory = () => {
  return useMutationWithToast({
    mutationFn: async (id: string) => {
      return api.delete("/trainings/categories/{category_id}", { path: { category_id: id } });
    },
    invalidateQueries: [
      ["training-categories"],
    ],
  });
};

// ========== Time Session Mutations ==========

export const useStartTimeSession = () =>
  useMutationWithToast({
    mutationFn: (trainingId: string) =>
      api.post("/trainings/{training_id}/time/start", { path: { training_id: trainingId } }),
  });


// ========== Certificados (§5.5) ==========


export const useCompletionCertificate = (completionId: string | null) =>
  useQuery({
    queryKey: ["training-certificate", completionId],
    enabled: Boolean(completionId),
    retry: false, // 404 = ainda não emitido; não re-tentar
    queryFn: () =>
      api.get("/trainings/completions/{completion_id}/certificate", { path: { completion_id: completionId! } }),
  });

export const useIssueCertificate = () => {
  return useMutationWithToast({
    mutationFn: (completionId: string) =>
      api.post("/trainings/completions/{completion_id}/issue-certificate", { path: { completion_id: completionId } }),
    invalidateQueries: (_, completionId) => [
      ["training-certificate", completionId],
    ],
  });
};

export async function downloadCertificate(certificateId: string): Promise<void> {
  const token = localStorage.getItem("access_token");
  const headers: Record<string, string> = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const res = await fetch(
    `${API_BASE_URL}/trainings/certificates/${certificateId}/download`,
    { credentials: "include", headers },
  );
  if (!res.ok) throw new Error(`Não foi possível descarregar o ficheiro: ${res.status}`);
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  const cd = res.headers.get("content-disposition") ?? "";
  const match = /filename="?([^";]+)"?/.exec(cd);
  a.download = match ? match[1].trim() : `certificado_${certificateId}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1_000);
}
